// Lightweight read-time / apply-time shape validation for entity rows.
//
// The Date-flatten bug (compactNode destroyed `createdDate` / `dueDate` into
// `{}` silently) taught us that structural corruption can persist for days
// before any user notices — no crash, no console error, just wrong data. The
// fix landed, but the class of bug will recur. This guard exists to catch
// the next instance at the storage boundary instead of at the screen that
// finally tries to render it.
//
// Philosophy:
//   - Be conservative. Only flag rows whose shape is *clearly* broken
//     (missing id, Date-typed fields reduced to empty objects). Optional
//     fields are not required.
//   - Don't filter on read — return all rows, log violations. UI rendering
//     decides how to degrade.
//   - On write/apply paths, a high violation ratio (>25% default) is treated
//     as probable corruption — refuse the apply, let the caller roll back
//     to a snapshot.
//   - Tombstones (`_deleted === true`) skip payload checks; they legitimately
//     lack fields.
//
// Used by:
//   - services/assetStorage.js — getItem path logs violations via secureLogger.
//   - services/cloudSyncService.js — applyCloudDataToLocal refuses apply when
//     the incoming blob exceeds the violation threshold.

// Default ratio of invalid rows above which a collection is treated as
// corrupted. Applied by cloudSyncService on apply; read path only logs.
export const CORRUPTION_REFUSAL_RATIO = 0.25;

// Keys that hold arrays of domain rows we can shape-check. Other keys (forms,
// primitives, color/bg) bypass the guard — trying to validate them would
// just add noise.
const ARRAY_ENTITY_KEYS = new Set([
  'invoices',
  'invoiceDetailList',
  'quotations',
  'quotationDetailList',
  'clients',
  'products',
]);

// Fields that MUST parse as a valid date if present on a row. When a date
// field becomes `{}` / `null` / `NaN`, we flag it — react-datepicker and
// moment will later throw on render, so catching it at the storage layer
// gives us a diagnosable breadcrumb.
const DATE_FIELDS_BY_KEY = {
  invoices: ['createdDate', 'dueDate'],
  invoiceDetailList: ['createdDate', 'dueDate'],
  quotations: ['createdDate', 'dueDate'],
  quotationDetailList: ['createdDate', 'dueDate'],
};

function isPlainEmptyObject(v) {
  return (
    v !== null &&
    typeof v === 'object' &&
    !Array.isArray(v) &&
    !(v instanceof Date) &&
    Object.keys(v).length === 0
  );
}

function isValidDateLike(v) {
  if (v == null) return true; // optional — absence is fine
  if (v instanceof Date) return !isNaN(v.getTime());
  if (typeof v === 'number') return isFinite(v);
  if (typeof v === 'string') {
    const d = new Date(v);
    return !isNaN(d.getTime());
  }
  // Objects / booleans / anything else — broken.
  return false;
}

// Validate a single row. Returns { valid, reasons: string[] }.
// Tombstones bypass payload checks.
export function validateRow(key, row) {
  if (!row || typeof row !== 'object' || Array.isArray(row)) {
    return { valid: false, reasons: ['not_object'] };
  }
  if (row._deleted === true) return { valid: true, reasons: [] };

  const reasons = [];

  if (typeof row.id !== 'string' || row.id.length === 0) {
    reasons.push('missing_id');
  }

  const dateFields = DATE_FIELDS_BY_KEY[key] || [];
  for (const f of dateFields) {
    if (!(f in row)) continue;
    const v = row[f];
    if (isPlainEmptyObject(v)) {
      reasons.push(`${f}:flattened_to_empty_object`);
    } else if (!isValidDateLike(v)) {
      reasons.push(`${f}:invalid_date`);
    }
  }

  return { valid: reasons.length === 0, reasons };
}

// Validate a whole collection (array). Returns summary + sample violations.
// Returns { checked: false } for keys that aren't array entities — callers
// can skip logging in that case.
export function validateCollection(key, rows) {
  if (!ARRAY_ENTITY_KEYS.has(key)) return { checked: false };
  if (!Array.isArray(rows)) {
    return {
      checked: true,
      key,
      total: 0,
      invalid: 0,
      ratio: 0,
      corrupted: false,
      samples: [],
      reason: 'not_array',
    };
  }
  const samples = [];
  let invalid = 0;
  for (let i = 0; i < rows.length; i++) {
    const { valid, reasons } = validateRow(key, rows[i]);
    if (!valid) {
      invalid++;
      if (samples.length < 5) {
        samples.push({
          index: i,
          id: rows[i]?.id,
          reasons,
        });
      }
    }
  }
  const total = rows.length;
  const ratio = total === 0 ? 0 : invalid / total;
  return {
    checked: true,
    key,
    total,
    invalid,
    ratio,
    corrupted: ratio > CORRUPTION_REFUSAL_RATIO,
    samples,
  };
}

// True when the key is one we know how to shape-check. Callers use this to
// skip the validate call for keys that would always return `{ checked: false }`.
export function isShapeCheckableKey(key) {
  return ARRAY_ENTITY_KEYS.has(key);
}
